import type {ReactElement} from 'react';
import {Image} from 'react-bootstrap';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {ClickableDiv} from '@Components/clickable-div';
import {Text, TextSize} from '@Components/text';
import {getAssetUrl} from '@Utils/s3.util';
import type {SocialMediaWizardHeaderProps} from './social-media-wizard-header.types';
import {WIZARD_LOGO_ASSET} from './social-media-wizard-header.types';
import styles from './social-media-wizard-header.module.scss';

export function SocialMediaWizardHeader({
  isSmallScreen,
  onBackClick,
  isBackDisabled = false,
  onLogoClick,
  desktopTitle,
  isDesktopTitleVisible,
  hasShadow,
  mobileTitle,
  rightSlot,
}: SocialMediaWizardHeaderProps): ReactElement {
  const renderLogo = (): ReactElement => {
    return (
      <ClickableDiv className="flex-row-align-center" onClick={onLogoClick}>
        <Image className="_cursor-pointer" src={getAssetUrl(WIZARD_LOGO_ASSET)} alt="PosterMyWall" />
      </ClickableDiv>
    );
  };

  const renderBackArrow = (className?: string): ReactElement | null => {
    if (!onBackClick) {
      return null;
    }

    return (
      <Icon
        className={className}
        onClick={onBackClick}
        isDisabled={isBackDisabled}
        icon="icon-arrow-left"
        size={IconSize.SIZE_ICON_20}
        shape={IconShape.SQUARE}
        type={IconType.GHOST}
        isUsedAsButton
        ariaLabel={window.i18next.t('pmwjs_back')}
      />
    );
  };

  const renderDesktopTitle = (): ReactElement => {
    const isHidden = !isDesktopTitleVisible || !desktopTitle;

    return <Text size={TextSize.MD} val={desktopTitle ?? ''} bold className={`${styles.desktopTitle} ${isHidden ? styles.hidden : ''}`} />;
  };

  const renderRightSlot = (): ReactElement | null => {
    return rightSlot ?? null;
  };

  const renderMobileLeadingGroup = (): ReactElement => {
    if (mobileTitle) {
      return (
        <div className={styles.mobileTitleGroup}>
          {renderBackArrow()}
          <Text size={TextSize.LG} val={mobileTitle} bold className={styles.mobileTitle} />
        </div>
      );
    }

    return (
      <div className={styles.mobileLogoGroup}>
        {renderBackArrow()}
        {renderLogo()}
      </div>
    );
  };

  const getHeaderClassName = (baseClass: string): string => {
    return [baseClass, hasShadow ? styles.headerContainerScrolled : ''].filter(Boolean).join(' ');
  };

  const getMobileHeaderTitleInsetClassName = (): string => {
    return mobileTitle && !onBackClick ? styles.mobileHeaderContainerTitleOnly : '';
  };

  if (isSmallScreen) {
    return (
      <header className={`${getHeaderClassName(styles.mobileHeaderContainer)} ${getMobileHeaderTitleInsetClassName()}`}>
        {renderMobileLeadingGroup()}
        {renderRightSlot()}
      </header>
    );
  }

  return (
    <header className={getHeaderClassName(styles.headerContainer)}>
      <div className={styles.leadingGroup}>
        {renderBackArrow()}
        {renderLogo()}
      </div>
      {renderDesktopTitle()}
      <div className={styles.actionsGroup}>{renderRightSlot()}</div>
    </header>
  );
}
